import { useEffect } from 'react';
import { SITE_ORIGIN } from '../seo';

/* Sets document.title, meta description, the canonical link, and the
   Open Graph / Twitter tags for the duration the component is mounted,
   then restores the previous values on unmount. Small SPA-friendly
   substitute for react-helmet for per-route SEO.

   `path` should be the route path ('/pricing'); it becomes the canonical
   URL and og:url. Without it, subpages would all claim the homepage as
   canonical and search engines would treat them as duplicates of '/'. */

function upsert(selector, create, attr, value) {
  let el = document.head.querySelector(selector);
  const existed = !!el;
  const prev = existed ? el.getAttribute(attr) : null;
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
  return () => {
    if (existed) el.setAttribute(attr, prev);
    else el.remove();
  };
}

const meta = (name) => () => {
  const el = document.createElement('meta');
  el.setAttribute('name', name);
  return el;
};
const metaProp = (property) => () => {
  const el = document.createElement('meta');
  el.setAttribute('property', property);
  return el;
};

export default function useDocumentMeta({ title, description, path } = {}) {
  useEffect(() => {
    const restores = [];
    const originalTitle = document.title;

    if (title) {
      document.title = title;
      restores.push(upsert('meta[property="og:title"]', metaProp('og:title'), 'content', title));
      restores.push(upsert('meta[name="twitter:title"]', meta('twitter:title'), 'content', title));
    }
    if (description) {
      restores.push(upsert('meta[name="description"]', meta('description'), 'content', description));
      restores.push(upsert('meta[property="og:description"]', metaProp('og:description'), 'content', description));
      restores.push(upsert('meta[name="twitter:description"]', meta('twitter:description'), 'content', description));
    }
    if (path) {
      const url = SITE_ORIGIN + path;
      restores.push(upsert('link[rel="canonical"]', () => {
        const el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        return el;
      }, 'href', url));
      restores.push(upsert('meta[property="og:url"]', metaProp('og:url'), 'content', url));
    }

    return () => {
      document.title = originalTitle;
      restores.forEach((r) => r());
    };
  }, [title, description, path]);
}
